import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, forwardRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus,
  CheckCircle2,
  Clock,
  Lock,
  Unlock,
  ArrowUpRight,
  X,
  Eye,
  Settings2,
} from "lucide-react";
import {
  EVAL_LEVELS,
  EVAL_LEVEL_LABELS,
  questionnaireInstanceOpenSchema,
  type QuestionnaireInstanceOpenInput,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { EvaluationsTabs } from "@/features/evaluations/EvaluationsTabs";

type EvalLevel = "client_user" | "organizational" | "team" | "project";
type EvalVisibility = "everyone" | "lead" | "finanzas" | "admin_only";

interface EvaluationRow {
  evaluation: {
    id: string;
    templateId: string;
    level: EvalLevel;
    projectId: string | null;
    period: string;
    createdAt: number;
    updatedAt: number;
    closedAt: number | null;
  };
  templateName: string;
  visibility: EvalVisibility;
}

interface Template {
  id: string;
  name: string;
  level: EvalLevel;
  visibility: EvalVisibility;
  active: boolean;
}

const filtersSchema = z.object({
  level: z.enum(["all", ...EVAL_LEVELS]).default("all"),
  status: z.enum(["all", "open", "closed"]).default("all"),
});

type Filters = z.input<typeof filtersSchema>;

export const Route = createFileRoute("/evaluations/questionnaires")({
  component: EvaluationsPage,
  validateSearch: filtersSchema,
});

function EvaluationsPage() {
  const filters = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const dialogRef = useRef<HTMLDialogElement>(null);
  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });
  const canOpen = me.data?.role === "admin" || me.data?.role === "lead";

  const { data, isLoading } = useQuery<{ evaluations: EvaluationRow[] }>({
    queryKey: ["evaluations", filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.level !== "all") params.set("level", filters.level);
      if (filters.status !== "all") params.set("status", filters.status);
      return api.get(`/questionnaire-instances?${params.toString()}`);
    },
  });

  const setFilter = (key: keyof Filters, value: string) =>
    navigate({
      to: ".",
      search: (prev: Filters) => ({ ...prev, [key]: value }),
      replace: true,
    });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="evaluación continua"
        title="Cuestionarios"
        subtitle="Encuestas que responde la gente — insumo cualitativo/cuantitativo subjetivo que alimenta los marcos de evaluación."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {me.data?.role === "admin" && (
              <Link to="/admin/evaluations-config" className="btn btn-ghost btn-sm">
                <Settings2 className="size-4" /> Config
              </Link>
            )}
            {canOpen && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => dialogRef.current?.showModal()}
              >
                <Plus className="size-4" /> Abrir cuestionario
              </button>
            )}
          </div>
        }
      />

      <EvaluationsTabs current="questionnaires" role={me.data?.role ?? "member"} />

      <div className="card-elevated p-3 flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Nivel"
          value={filters.level}
          onChange={(v) => setFilter("level", v)}
          options={[
            { value: "all", label: "Todos" },
            ...EVAL_LEVELS.map((l) => ({ value: l, label: EVAL_LEVEL_LABELS[l] })),
          ]}
        />
        <FilterSelect
          label="Estado"
          value={filters.status}
          onChange={(v) => setFilter("status", v)}
          options={[
            { value: "all", label: "Todas" },
            { value: "open", label: "Abiertas" },
            { value: "closed", label: "Cerradas" },
          ]}
        />
        <div className="ml-auto text-sm text-base-content/55">
          {data ? `${data.evaluations.length} evaluaciones` : null}
        </div>
      </div>

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {data && data.evaluations.length === 0 ? (
        <div className="card-elevated py-16 text-center">
          <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
            <CheckCircle2 className="size-6 text-primary" />
          </div>
          <h3 className="font-display font-semibold text-base-content mb-1">
            Sin evaluaciones todavía
          </h3>
          <p className="text-sm text-base-content/60 mb-5">
            {canOpen
              ? "Abre la primera desde una plantilla."
              : "Cuando tu lead/admin abra una, aparecerá aquí."}
          </p>
        </div>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data?.evaluations.map((row) => (
            <EvaluationCard key={row.evaluation.id} row={row} />
          ))}
        </ul>
      )}

      <OpenEvaluationDialog ref={dialogRef} onClose={() => dialogRef.current?.close()} />
    </div>
  );
}

function EvaluationCard({ row }: { row: EvaluationRow }) {
  const isOpen = row.evaluation.closedAt === null;
  const isRestricted = row.visibility !== "everyone";
  return (
    <li>
      <Link
        to="/evaluations/$id"
        params={{ id: row.evaluation.id }}
        className="card-elevated p-4 group flex flex-col gap-3 hover:[&]:card-elevated-hover h-full"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`badge badge-sm ${badgeForLevel(row.evaluation.level)}`}>
                {EVAL_LEVEL_LABELS[row.evaluation.level]}
              </span>
              {isRestricted && (
                <span title="Visibilidad restringida — no todos ven respuestas individuales">
                  <Lock className="size-3.5 text-base-content/40" />
                </span>
              )}
            </div>
            <h3 className="font-display font-semibold text-base-content leading-snug">
              {row.templateName}
            </h3>
            <div className="text-xs text-base-content/55 mt-0.5">
              Periodo {row.evaluation.period}
            </div>
          </div>
          <ArrowUpRight className="size-4 text-base-content/30 group-hover:text-primary transition-colors shrink-0 mt-1" />
        </div>
        <div className="pt-2 border-t border-base-300/50 flex items-center justify-between">
          {isOpen ? (
            <span className="text-xs text-success inline-flex items-center gap-1">
              <Unlock className="size-3" /> Abierta · responde
            </span>
          ) : (
            <span className="text-xs text-base-content/55 inline-flex items-center gap-1">
              <Lock className="size-3" /> Cerrada el {formatDate(row.evaluation.closedAt!)}
            </span>
          )}
        </div>
      </Link>
    </li>
  );
}

// ─── Open evaluation dialog ────────────────────────────────────────────────

const OpenEvaluationDialog = forwardRef<HTMLDialogElement, { onClose: () => void }>(
  function OpenEvaluationDialog({ onClose }, ref) {
    const qc = useQueryClient();
    const navigate = useNavigate();

    const templatesQuery = useQuery<{ templates: Template[] }>({
      queryKey: ["evaluation-templates"],
      queryFn: () => api.get("/questionnaires"),
    });

    const projectsQuery = useQuery<{ projects: { id: string; name: string }[] }>({
      queryKey: ["projects"],
      queryFn: () => api.get("/projects"),
    });

    const {
      register,
      handleSubmit,
      reset,
      formState: { errors },
    } = useForm<QuestionnaireInstanceOpenInput>({
      resolver: zodResolver(questionnaireInstanceOpenSchema),
      defaultValues: { period: defaultPeriod() },
    });

    const open = useMutation({
      mutationFn: (input: QuestionnaireInstanceOpenInput) =>
        api.post<{ id: string }>("/questionnaire-instances", input),
      onSuccess: ({ id }) => {
        qc.invalidateQueries({ queryKey: ["evaluations"] });
        reset();
        onClose();
        navigate({ to: "/evaluations/$id", params: { id } });
      },
    });

    return (
      <dialog ref={ref} className="modal" onClose={() => reset()}>
        <div className="modal-box card-elevated">
          <h3 className="text-lg font-display font-semibold mb-4">Abrir evaluación</h3>
          <form onSubmit={handleSubmit((d) => open.mutate(d))} className="space-y-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Plantilla</span>
              </label>
              <select className="select select-bordered w-full" {...register("questionnaireId")}>
                <option value="">Selecciona una plantilla…</option>
                {(templatesQuery.data?.templates ?? [])
                  .filter((t) => t.active)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      [{EVAL_LEVEL_LABELS[t.level]}] {t.name}
                    </option>
                  ))}
              </select>
              {errors.questionnaireId && (
                <span className="text-xs text-error mt-1 block">
                  {errors.questionnaireId.message}
                </span>
              )}
            </div>

            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Periodo</span>
              </label>
              <input
                className="input input-bordered w-full font-mono text-sm"
                placeholder="2026-Q2 o 2026-05"
                {...register("period")}
              />
              {errors.period && (
                <span className="text-xs text-error mt-1 block">{errors.period.message}</span>
              )}
            </div>

            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Proyecto (opcional)</span>
              </label>
              <select className="select select-bordered w-full" {...register("projectId")}>
                <option value="">— ninguno —</option>
                {(projectsQuery.data?.projects ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-base-300/60">
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={open.isPending}>
                {open.isPending && <span className="loading loading-spinner loading-xs" />}
                Abrir
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>cerrar</button>
        </form>
      </dialog>
    );
  },
);

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-xs uppercase tracking-wider text-base-content/55 hidden sm:inline">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="select select-bordered select-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function defaultPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function badgeForLevel(level: EvalLevel): string {
  switch (level) {
    case "client_user":
      return "badge-info";
    case "organizational":
      return "badge-secondary";
    case "team":
      return "badge-accent";
    case "project":
      return "badge-primary";
  }
}

// Hack para que oxlint no se queje de imports no usados arriba
void Eye;
void X;
void Clock;
